import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle2, Clock, Loader2, Mail, ShieldAlert, XCircle } from "lucide-react";
import {
  outcomeLabel,
  errorCategoryLabel,
} from "@/components/master/_labels";

/**
 * Phase 36 — "Send reset link" confirmation modal.
 *
 * Backend contract (from execute_reset_pipeline):
 *   outcome ∈ { provider_accepted, provider_failed, native_login_disabled,
 *               disabled_account, duplicate_identity_rejected, rate_limited,
 *               account_not_found, action_needed, identity_integrity_error }
 *
 * Raw enum stays in `data-outcome` for CSS/Playwright; the visible
 * label comes from `_labels.js`. "provider_accepted" renders as
 * "Accepted for sending" — never claim "Delivered".
 */
const OUTCOME_META = {
  provider_accepted: { color: "bg-secondary/15 text-secondary", icon: CheckCircle2 },
  provider_failed: { color: "bg-destructive/10 text-destructive", icon: XCircle },
  native_login_disabled: { color: "bg-muted text-foreground", icon: ShieldAlert },
  disabled_account: { color: "bg-muted text-foreground", icon: ShieldAlert },
  duplicate_identity_rejected: { color: "bg-destructive/10 text-destructive", icon: AlertTriangle },
  rate_limited: { color: "bg-muted text-foreground", icon: Clock },
  account_not_found: { color: "bg-muted text-foreground", icon: AlertTriangle },
  action_needed: { color: "bg-destructive/10 text-destructive", icon: AlertTriangle },
  identity_integrity_error: { color: "bg-destructive/10 text-destructive", icon: AlertTriangle },
};

export default function SendResetLinkDialog({ open, onClose, tenantId, tenantSlug, member }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setReason(""); setResult(null); };

  const close = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!member?.user_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/master/tenants/${tenantId}/members/${member.user_id}/send-reset-link`,
        { reason: reason.trim() || "manual-recovery" },
      );
      setResult(data);
      if (data.outcome === "provider_accepted") {
        toast.success(`Reset link accepted for sending to ${data.recipient_email}`);
      } else if (data.outcome === "rate_limited") {
        toast.error("Rate limit hit — try again later.");
      } else {
        toast(outcomeLabel(data.outcome));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const meta = result ? OUTCOME_META[result.outcome] : null;
  const Icon = meta?.icon;
  const label = result ? outcomeLabel(result.outcome) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="send-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-secondary" />
            Send reset link
          </DialogTitle>
          <DialogDescription>
            Triggers the canonical recovery pipeline for{" "}
            <span className="font-mono text-xs">{member?.email || "—"}</span>.
            Not a bypass of password policy, MFA, or disabled-account checks.
            Rate-limited to 5 per workspace per hour.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-reason">Reason</Label>
              <Textarea
                id="reset-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. member reports they didn't receive the self-service reset email"
                required
                minLength={3}
                maxLength={500}
                rows={3}
                data-testid="send-reset-reason"
              />
            </div>
            {member?.auth_mode === "federated" && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This member looks federated — if their tenant's auth_mode is
                  "federated" and they have no local password, the pipeline
                  will return "Native login disabled" and no mail will be sent.
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="send-reset-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send reset link
              </Button>
            </DialogFooter>
          </form>
        )}

        {result && (
          <div className="space-y-4" data-testid="send-reset-result">
            <div className="flex items-center gap-3 rounded-xl border p-4">
              {Icon && <Icon className={`h-6 w-6 ${meta.color.includes("text-") ? meta.color.split(" ").pop() : ""}`} />}
              <div className="flex-1">
                <div className="font-medium">{label || result.outcome}</div>
                <div className="text-xs text-muted-foreground">
                  Attempt id: <code className="font-mono">{result.attempt_id || "—"}</code>
                </div>
              </div>
              <Badge
                className={meta?.color || "bg-muted"}
                data-testid="send-reset-outcome-badge"
                data-outcome={result.outcome}
              >
                {label}
              </Badge>
            </div>
            <div className="space-y-1 rounded-xl bg-muted/40 p-3 text-xs">
              <Row label="Recipient" value={result.recipient_email || "—"} />
              <Row label="Provider message id" value={result.provider_message_id || "—"} />
              <Row label="Error category" value={errorCategoryLabel(result.error_category)} />
              <Row label="Attributed tenant" value={result.requested_tenant_slug || tenantSlug} />
            </div>
            <DialogFooter>
              <Button onClick={close} data-testid="send-reset-close">Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate font-mono text-foreground">{String(value)}</span>
    </div>
  );
}
